import jsPDF from 'jspdf';
import { TestResults, UserDetails } from '../types';
import { CoverPageComponent } from './pdfGenerator/components/CoverPage';
import { CategoryResultsComponent } from './pdfGenerator/components/CategoryResults';
import { VisualizationsComponent } from './pdfGenerator/components/Visualizations';
import { RecommendationsComponent } from './pdfGenerator/components/Recommendations';
import { FooterComponent } from './pdfGenerator/components/Footer';
import { PageManager } from './pdfGenerator/helpers/pageBreaks';

export const generatePDF = async (
  results: TestResults,
  userDetails: UserDetails | null
): Promise<void> => {
  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageManager = new PageManager(pdf);

    const coverPage = new CoverPageComponent(pdf, pageManager);
    const categoryResults = new CategoryResultsComponent(pdf, pageManager);
    const visualizations = new VisualizationsComponent(pdf, pageManager);
    const recommendations = new RecommendationsComponent(pdf, pageManager);
    const footer = new FooterComponent(pdf);

    coverPage.generate(results, userDetails);

    pdf.addPage();
    pageManager.resetToTop();
    visualizations.addOverviewChart(results);

    visualizations.addScoreDistribution(results);
    visualizations.addBenchmarkComparison(results);

    pdf.addPage();
    pageManager.resetToTop();
    categoryResults.generate(results);

    pdf.addPage();
    pageManager.resetToTop();
    recommendations.generate(results);

    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      if (i > 1) {
        footer.addFooter(userDetails, i);
      }
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const userName = userDetails?.fullName
      ? userDetails.fullName.replace(/[^a-zA-Z0-9]/g, '_')
      : 'Export_Readiness';

    const filename = `${userName}_Export_Readiness_Report_${timestamp}.pdf`;
    pdf.save(filename);
    console.log('PDF generated successfully:', filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('There was an error generating the PDF report. Please try again.');
    throw error;
  }
};


export const validatePDFData = (results: TestResults, userDetails: UserDetails | null): boolean => {
  const issues = [];

  if (!results) issues.push('No results data provided');
  if (!results?.categories || results.categories.length === 0) issues.push('No category results found');
  if (typeof results?.overallPercentage !== 'number') issues.push('Invalid overall percentage');
  if (!results?.overallStatus) issues.push('No overall status found');

  if (issues.length > 0) {
    console.error('PDF Validation Errors:', issues.join('; '));
    return false;
  }

  return true;
};

export const generateValidatedPDF = async (results: TestResults, userDetails: UserDetails | null): Promise<void> => {
  if (!validatePDFData(results, userDetails)) {
    alert('Invalid data provided for PDF generation. Please check results and user details.');
    throw new Error('Validation failed');
  }

  try {
    await generatePDF(results, userDetails);
  } catch (error: any) {
    console.error('PDF Generation failed:', error);
    alert(`PDF generation failed: ${error?.message || 'Unknown error'}`);
    throw new Error('Failed to generate PDF report. Please try again.');
  }
};

export default generateValidatedPDF;
